<template>
  <header class="site-header" :class="{ 'is-scrolled': scrolled }">
    <div class="container site-header-row">
      <BrandMark :eager="true" />

      <nav class="site-nav" :class="{ 'is-open': mobileOpen }" @click="closeMobile">
        <RouterLink
          v-for="l in NAV_LINKS"
          :key="l.labelKey"
          :to="l.to"
          class="site-nav-link"
        >
          {{ c.nav[l.labelKey] }}
        </RouterLink>
        <SmartCta
          v-if="isAuthenticated"
          mode="dashboard"
          variant="link"
          label="Dashboard"
          class="site-nav-link site-nav-link-mobile-only"
        />
        <SmartCta v-else mode="login" variant="link" label="Login" class="site-nav-link site-nav-link-mobile-only" />
        <SmartCta variant="primary" size="sm" class="site-nav-link-mobile-only site-nav-cta-mobile" />
      </nav>

      <div class="site-header-cta">
        <LanguageSwitcher />
        <SmartCta v-if="isAuthenticated" mode="dashboard" variant="link" label="Dashboard" />
        <SmartCta v-else mode="login" variant="link" label="Login" />
        <SmartCta variant="primary" size="sm" />
      </div>

      <button
        class="site-burger"
        :aria-expanded="mobileOpen"
        aria-label="Open menu"
        @click="mobileOpen = !mobileOpen"
      >
        <span class="site-burger-bar" :class="{ 'is-open': mobileOpen }" />
      </button>
    </div>
  </header>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import BrandMark from './BrandMark.vue';
import SmartCta from './SmartCta.vue';
import LanguageSwitcher from './LanguageSwitcher.vue';
import { NAV_LINKS } from '../config/site.js';
import { useSession } from '../composables/useSession.js';
import { useLocale } from '../composables/useLocale.js';

const { isAuthenticated } = useSession();
const { content: c } = useLocale();
const scrolled = ref(false);
const mobileOpen = ref(false);

function onScroll() {
  scrolled.value = window.scrollY > 32;
}
function closeMobile() {
  mobileOpen.value = false;
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
});
</script>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 50;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  background: rgba(255, 255, 255, 0.72);
  transition:
    background 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease;
  border-bottom: 1px solid transparent;
}
.site-header.is-scrolled {
  background: rgba(255, 255, 255, 0.92);
  border-bottom-color: var(--border);
  box-shadow: var(--shadow-sm);
}
.site-header-row {
  display: flex;
  align-items: center;
  gap: 24px;
  height: 64px;
}
.site-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 28px;
  flex: 1;
}
.site-nav-link {
  color: var(--fg-soft);
  font-size: 14px;
  font-weight: 500;
  padding: 8px 14px;
  border-radius: 999px;
  transition:
    color 120ms ease,
    background 120ms ease;
  text-decoration: none;
}
.site-nav-link:hover {
  color: var(--fg);
  background: var(--bg-elev);
}
.site-header-cta {
  display: flex;
  align-items: center;
  gap: 12px;
}
.site-burger {
  display: none;
  background: none;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  width: 40px;
  height: 40px;
  cursor: pointer;
  padding: 0;
  position: relative;
}
.site-burger-bar,
.site-burger-bar::before,
.site-burger-bar::after {
  position: absolute;
  left: 50%;
  width: 18px;
  height: 2px;
  background: var(--fg);
  border-radius: 2px;
  transition:
    transform 160ms ease,
    top 160ms ease,
    opacity 160ms ease;
  transform: translateX(-50%);
}
.site-burger-bar {
  top: 50%;
  margin-top: -1px;
}
.site-burger-bar::before,
.site-burger-bar::after {
  content: '';
  top: -6px;
}
.site-burger-bar::after {
  top: 6px;
}
.site-burger-bar.is-open {
  background: transparent;
}
.site-burger-bar.is-open::before {
  top: 0;
  transform: translateX(-50%) rotate(45deg);
}
.site-burger-bar.is-open::after {
  top: 0;
  transform: translateX(-50%) rotate(-45deg);
}
.site-nav-link-mobile-only {
  display: none;
}
.site-nav-cta-mobile {
  margin-top: 8px;
  width: 100%;
  justify-content: center;
}

@media (max-width: 880px) {
  .site-burger {
    display: inline-block;
  }
  .site-header-cta {
    display: none;
  }
  .site-nav {
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
    gap: 6px;
    margin: 0;
    transform: translateY(-110%);
    transition: transform 200ms ease;
    box-shadow: var(--shadow-md);
  }
  .site-nav.is-open {
    transform: translateY(0);
  }
  .site-nav-link {
    padding: 12px 14px;
    font-size: 15px;
  }
  .site-nav-link-mobile-only {
    display: inline-flex;
  }
}
</style>
